import { defineComponent } from "../lib/component.ts";

interface ArticleHeroVisualRefs {
  tablist?: HTMLElement;
}

class ArticleHeroVisual extends defineComponent<ArticleHeroVisualRefs>()(["tablist"]) {
  #activeIndex = 0;

  #preSelectIndex = 0;

  #tabs: HTMLButtonElement[] = [];

  #panels: HTMLElement[] = [];

  connectedCallback(): void {
    super.connectedCallback();
    this.#tabs = [];
    this.#activeIndex = 0;

    this.#panels = [];
    for (const el of this.querySelectorAll(".article-hero-visual__item[data-title]")) {
      if (el instanceof HTMLElement) this.#panels.push(el);
    }

    if (this.#panels.length === 0) return;

    this.#buildTabs();
    this.#enhancePanels();
    this.#hideLabels();
    this.#activate(0);
    this.classList.add("is-initialized");

    this.refs.tablist.addEventListener("keydown", this.#onKeyDown, {
      signal: this.signal,
    });

    document.addEventListener("shopify:block:select", this.#onBlockSelect, {
      signal: this.signal,
    });

    document.addEventListener("shopify:block:deselect", this.#onBlockDeselect, {
      signal: this.signal,
    });
  }

  #buildTabs(): void {
    const tablist = this.refs.tablist;
    tablist.setAttribute("role", "tablist");
    tablist.setAttribute("aria-orientation", "vertical");
    tablist.setAttribute("aria-label", this.dataset.tabLabel ?? "");

    const baseId = this.id;

    for (let i = 0; i < this.#panels.length; i++) {
      const title = this.#panels[i].dataset.title ?? "";

      const button = document.createElement("button");
      button.type = "button";
      button.role = "tab";
      button.id = `${baseId}-tab-${i}`;
      button.setAttribute("aria-controls", `${baseId}-panel-${i}`);
      button.setAttribute("aria-selected", "false");
      button.tabIndex = -1;
      button.textContent = title;
      button.className = "article-hero-visual__tab tap-target";
      button.addEventListener("click", () => this.#activate(i), {
        signal: this.signal,
      });

      tablist.appendChild(button);
      this.#tabs.push(button);
    }
  }

  #enhancePanels(): void {
    const baseId = this.id;
    for (let i = 0; i < this.#panels.length; i++) {
      const panel = this.#panels[i];
      panel.setAttribute("role", "tabpanel");
      panel.id = `${baseId}-panel-${i}`;
      panel.setAttribute("aria-labelledby", `${baseId}-tab-${i}`);
    }
  }

  #hideLabels(): void {
    for (const label of this.querySelectorAll(
      ".article-hero-visual__item .article-hero-visual__tab",
    )) {
      if (label instanceof HTMLElement) label.hidden = true;
    }
  }

  #activate(index: number): void {
    this.#activeIndex = index;

    for (let i = 0; i < this.#panels.length; i++) {
      const active = i === index;
      const tab = this.#tabs[i];
      const panel = this.#panels[i];

      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
      panel.dataset.active = String(active);
      panel.setAttribute("aria-hidden", String(!active));

      this.#toggleMedia(panel, active);
    }
  }

  #toggleMedia(container: HTMLElement, active: boolean): void {
    const video = container.querySelector("video");
    if (!video) return;
    video.removeAttribute("autoplay");
    if (active) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  #onKeyDown = (e: KeyboardEvent): void => {
    const count = this.#tabs.length;
    let index = this.#activeIndex;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        index = (index + 1) % count;
        break;
      case "ArrowUp":
        e.preventDefault();
        index = (index - 1 + count) % count;
        break;
      case "Home":
        e.preventDefault();
        index = 0;
        break;
      case "End":
        e.preventDefault();
        index = count - 1;
        break;
      default:
        return;
    }

    this.#activate(index);
    this.#tabs[index].focus();
  };

  #onBlockSelect = (e: DocumentEventMap["shopify:block:select"]): void => {
    const { blockId } = e.detail;

    for (let i = 0; i < this.#panels.length; i++) {
      if (this.#panels[i].dataset.blockId === blockId) {
        this.#preSelectIndex = this.#activeIndex;
        this.#activate(i);
        return;
      }
    }
  };

  #onBlockDeselect = (e: DocumentEventMap["shopify:block:deselect"]): void => {
    const { blockId } = e.detail;

    for (const panel of this.#panels) {
      if (panel.dataset.blockId === blockId) {
        this.#activate(this.#preSelectIndex);
        return;
      }
    }
  };
}

if (!customElements.get("article-hero-visual")) {
  customElements.define("article-hero-visual", ArticleHeroVisual);
}
